import { Component, NgZone, OnDestroy, OnInit, effect, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, PopoverController, ToastController } from '@ionic/angular';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { ModalComponent } from 'src/app/components/modals/modal/modal.component';
import { PopoverComponent } from 'src/app/components/popovers/popover/popover.component';
import { ErrorService } from 'src/app/services/error/error.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PagingService } from 'src/app/services/paging/paging.service';
import { IPrinter } from 'src/app/services/printer/models/printer.model';
import { IQueue } from 'src/app/services/printer/models/queue.model';
import { PrinterService } from 'src/app/services/printer/printer.service';
import { SanitizationService } from 'src/app/services/sanitization/sanitization.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { UserService } from 'src/app/services/user/user.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { DocumentPrintService } from 'src/app/services/document-print/document-print.service';
import { UploadState } from 'src/app/services/document-print/models/document-upload-session.model';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { ShareExtensionService } from 'src/app/services/share-extension/share-extension.service';

@Component({
  selector: 'app-document-print',
  templateUrl: './document-print.page.html',
  styleUrls: ['./document-print.page.scss'],
  standalone: false
})
export class DocumentPrintPage implements OnInit, OnDestroy {

  // ############### SIGNALS ###############

  public selectedFile         = signal<File | null>(null);
  public selectedQueue        = signal<IQueue | null>(null);
  public queueList            = signal<IQueue[]>([]);
  public pagingObject         = signal<any>({});
  public storedNetworkFilters = signal<any[]>([]);
  public isDragOver           = signal<boolean>(false);
  public validationError      = signal<string | null>(null);
  public isOnline             = this.networkService.hasInternetAccess;

  // ############### PAGINATION / FILTER STATE ###############

  public currentPage  = 0;
  private pageSize    = 9;
  private searchTerm  = '';
  private storedNetworkIds: string[] = [];
  private tenantNetworks: any;
  private printerListData: IPrinter[] = [];
  private filterObjectsKey = 'filter_objects';

  public get uploadState(): UploadState {
    return this.documentPrintService.uploadState();
  }

  // ############### INIT ###############

  private logger: Logger;
  private destroy$ = new Subject<void>();
  // True while this page is visible; guards the pendingFilePath effect so it
  // only triggers an immediate load when the page is already on screen.
  private isPageActive = false;

  constructor(
    private documentPrintService: DocumentPrintService,
    private errorService: ErrorService,
    private loggingService: LoggingService,
    private modalCtrl: ModalController,
    private networkService: NetworkService,
    private toastCtrl: ToastController,
    private ngZone: NgZone,
    private pagingService: PagingService,
    private popoverCtrl: PopoverController,
    private printerService: PrinterService,
    private router: Router,
    private sanitizationService: SanitizationService,
    private storageService: StorageService,
    private tenantService: TenantService,
    private userService: UserService,
    private pageTitleService: PageTitleService,
    private platformService: PlatformService,
    private shareExtensionService: ShareExtensionService,
  ) {
    this.logger = this.loggingService.getLogger("[DocumentPrintPage]");
    this.logger.entry("ctor");

    // If the user shares a file while already on this page (no navigation occurs,
    // so ionViewDidEnter won't re-fire), the effect detects the signal change and
    // loads the file immediately. The isPageActive guard prevents loading before
    // the page has fully entered for the first time.
    effect(() => {
      const filePath = this.shareExtensionService.pendingFilePath();
      if (filePath && this.isPageActive) {
        this.logger.info('ShareExtension: page active, loading pending shared file');
        this.loadPendingSharedFile(filePath);
      }
    }, { allowSignalWrites: true });
  }

  ngOnInit(): void {
    const methodName = "ngOnInit";
    this.logger.info(methodName);
    this.pageTitleService.setTitle('Upload For Printing');
    this.documentPrintService.reset();
    this.tenantService.refreshStoredTenant()
      .pipe(
        tap(() => this.logger.info(methodName + ': tenant refreshed')),
        switchMap(() => this.userService.refreshCurrentUser()),
        tap(() => this.logger.info(methodName + ': user refreshed')),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.loadPrinters(),
        error: (err) => {
          this.logger.error(methodName, 'tenant/user refresh failed: ' + String(err));
          this.errorService.handleHttpClientResponseError(err, 'GET', methodName).subscribe();
        }
      });
  }

  // "Upload For Printing" button is shown for every new job. Transient in-flight
  // states (initiating/uploading/confirming) are left untouched so an upload that
  // is still running is not disrupted.
  ionViewWillEnter(): void {
    const methodName = "ionViewWillEnter";
    const state = this.uploadState;
    if (state === 'done' || state === 'error') {
      this.logger.info(methodName + ': resetting uploadState from ' + state + ' to idle');
      this.documentPrintService.reset();
      this.selectedFile.set(null);
      this.selectedQueue.set(null);
      this.validationError.set(null);
    }
  }

  // Fires after the page enter animation completes — the page is fully stable.
  // Primary path for consuming a pending shared file after navigation.
  ionViewDidEnter(): void {
    this.isPageActive = true;

    if (!this.platformService.isIOS()) { return; }

    const filePath = this.shareExtensionService.pendingFilePath();
    if (filePath) {
      this.logger.info('ShareExtension: ionViewDidEnter loading pending shared file');
      this.loadPendingSharedFile(filePath);
    }
  }

  ionViewWillLeave(): void {
    this.logger.info('Share Extension: ionViewWillLeave — isPageActive=false uploadState=' + this.uploadState);
    this.isPageActive = false;
    // Clean up the shared file regardless of outcome so stale App Group files
    // do not retrigger the share flow on the next app launch or resume.
    // deleteSharedFile() is idempotent — it is a no-op when pendingFilename is null
    // (i.e. upload already succeeded, or no share extension file was involved).
    if (this.platformService.isIOS()) {
      this.shareExtensionService.deleteSharedFile();
    }
  }

  ngOnDestroy(): void {
    const methodName = "ngOnDestroy";
    this.logger.info(methodName);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ############### PRINTER / QUEUE ###############

  private loadPrinters(): void {
    const methodName = "loadPrinters";
    this.logger.info(methodName);

    const tenant = this.tenantService.tenantSignal();
    if (!tenant?.links?.networks) {
      this.fetchQueues();
      return;
    }

    this.tenantService.getTenantNetworks(tenant.links.networks, [], 0, 5, 'NAME', 'ASC', '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (networksList) => {
          this.tenantNetworks = networksList;
          this.storageService.getItemFromLocalStorage(this.filterObjectsKey, 'loadPrinters()')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (stored: any[]) => {
                stored = stored ?? [];
                this.storedNetworkFilters.set(stored);
                this.storedNetworkIds = stored.map(nw => nw['id']);
                this.fetchQueues();
              },
              error: () => this.fetchQueues()
            });
        },
        error: () => this.fetchQueues()
      });
  }

  private fetchQueues(): void {
    const methodName = "fetchQueues";
    this.logger.info(methodName);

    const queueParameters = this.pagingService.getQueueParameters(this.currentPage, this.pageSize, [], this.searchTerm || '', []);
    queueParameters.printerTypes = ['NETWORK', 'VIRTUAL', 'FREEDOM'];
    this.storedNetworkFilters().forEach(nw => queueParameters.networks.push(nw['id']));

    this.printerService.getPrinterQueuesList(queueParameters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any[]) => {
          const pageObj     = result.pop();
          const printersObj = result.pop();
          const queues: IQueue[] = result;
          this.printerListData = printersObj?.printers ?? [];
          this.setPrinterDetailsOnQueues(queues, this.printerListData);
          this.queueList.set(queues.filter(q => q?.name));
          this.pagingObject.set(pageObj?.page ?? {});
        },
        error: (err) => {
          this.errorService.handleHttpClientResponseError(err, 'POST', methodName);
        }
      });
  }

  private setPrinterDetailsOnQueues(queues: IQueue[], printers: IPrinter[]): void {
    const methodName = "setPrinterDetailsOnQueues";
    this.logger.info(methodName);
    queues.forEach(q => {
      printers.forEach(p => {
        if (q.signId === p.signId) {
          q.printerDetails.location = p.location as unknown as string;
          q.printerDetails.colorSupport = p.colorSupport;
          q.printerDetails.duplexSupport = p.duplexSupport;
        }
      });
    });
  }

  public onQueueSelect(queue: IQueue): void {
    const methodName = "onQueueSelect";
    this.logger.info(methodName);
    this.selectedQueue.set(queue);
  }

  public getNextPage(showPrevious = false): void {
    const methodName = "getNextPage";
    this.logger.info(methodName);
    showPrevious ? this.currentPage-- : this.currentPage++;
    this.fetchQueues();
  }

  public dismissSelectedFilter(filter: any): void {
    const methodName = "dismissSelectedFilter";
    this.logger.info(methodName);
    const updated = this.storedNetworkFilters().filter(f => f !== filter);
    this.storedNetworkFilters.set(updated);
    this.storedNetworkIds = updated.map(nw => nw['id']);
    this.storageService.addItemToLocalStorage(this.filterObjectsKey, updated)
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: () => { this.currentPage = 0; this.fetchQueues(); } });
  }

  public async presentPopover(myEvent: Event): Promise<void> {
    const methodName = "presentPopover";
    this.logger.info(methodName);

    const dataPackage = {
      type: 'filterPrinterList',
      networks: this.tenantNetworks,
      resourceId: null,
      storedNetworks: this.storedNetworkFilters()
    };

    const popover = await this.popoverCtrl.create({
      component: PopoverComponent,
      event: myEvent,
      translucent: true,
      componentProps: { dataPackage },
      cssClass: 'filterPrinterList',
      animated: true
    });

    await popover.present();

    await popover.onDidDismiss().then((result) => {
      if (result.data === 'clearAllFilters') {
        this.storageService.removeStoredItems([this.filterObjectsKey])
          .pipe(takeUntil(this.destroy$))
          .subscribe({ next: () => { this.currentPage = 0; this.storedNetworkFilters.set([]); this.storedNetworkIds = []; this.fetchQueues(); } });
      } else if (result.data === 'showNetworksModal') {
        this.presentNetworksListModal();
      }
    });
  }

  private async presentNetworksListModal(): Promise<void> {
    const methodName = "presentNetworksListModal";
    this.logger.info(methodName);

    const dataPackage = {
      networks: this.tenantNetworks,
      resourceId: null,
      storedNetworks: this.storedNetworkFilters(),
      cssClass: 'networksList'
    };

    const modal = await this.modalCtrl.create({
      component: ModalComponent,
      componentProps: { dataPackage },
      cssClass: 'networksList'
    });

    await modal.present();

    await modal.onDidDismiss().then(() => {
      this.storageService.getItemFromLocalStorage(this.filterObjectsKey, methodName)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (stored: any[]) => {
            stored = stored ?? [];
            this.storedNetworkFilters.set(stored);
            this.storedNetworkIds = stored.map(nw => nw['id']);
            this.currentPage = 0;
            this.fetchQueues();
          }
        });
    });
  }

  public onSearch(event: CustomEvent): void {
    const methodName = "onSearch";
    this.logger.info(methodName);
    this.searchTerm = this.sanitizationService.sanitizeTextInput(event.detail.value ?? '');
    this.currentPage = 0;
    this.fetchQueues();
  }

  // ############### SHARE EXTENSION ###############

  // Reads the file from Documents/ImportedShareExtension/ and feeds it into the
  // normal file-selection path. Called by ionViewDidEnter (post-navigation) and
  // by the pendingFilePath effect (already-on-page case).
  // Always resets upload state first so re-entry after a previous upload
  // (uploadState='done') or failed upload (uploadState='error') shows the button.
  private loadPendingSharedFile(filePath: string): void {
    this.documentPrintService.reset();
    const filename = filePath.split('/').pop() ?? filePath;
    this.logger.info('ShareExtension: loadPendingSharedFile filename=' + this.sanitizationService.sanitizeLogOutput(filename));

    this.shareExtensionService.readFileAsJSFile(filename)
      .then(file => {
        this.logger.info('ShareExtension: shared file loaded name=' + this.sanitizationService.sanitizeLogOutput(file.name) + ' size=' + file.size);
        this.ngZone.run(() => {
          this.setFile(file);
          this.shareExtensionService.clearPendingFilePath();
        });
      })
      .catch(err => {
        const msg = String(err);
        this.logger.error('loadPendingSharedFile', 'ShareExtension: FAILED — ' + msg);
        this.shareExtensionService.resetPendingShareAfterReadFailure();
        this.toastCtrl.create({
          message: 'Could not load shared file. Please try sharing again.',
          color: 'danger',
          duration: 4000,
          position: 'top'
        }).then(t => t.present());
      });
  }

  // ############### FILE PICKER ###############

  public onFileInputChange(event: Event): void {
    const methodName = "onFileInputChange";
    this.logger.info(methodName);

    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const rawFile = input.files[0];

    const error = this.documentPrintService.validateFile(rawFile);
    if (error) {
      this.validationError.set(error);
      this.selectedFile.set(null);
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const arrayBuffer = e.target!.result as ArrayBuffer;
        const blob = new Blob([arrayBuffer], { type: rawFile.type || 'application/pdf' });
        const file = Object.assign(blob, {
          name: rawFile.name,
          lastModified: rawFile.lastModified
        }) as unknown as File;

        this.ngZone.run(() => {
          this.setFile(file);
        });
      } catch (err) {
        this.logger.error(methodName, 'Failed to read file — ' + String(err));
        this.ngZone.run(() => this.validationError.set('FileReadError'));
      }
    };

    reader.onerror = () => {
      this.logger.error(methodName, 'FileReader error');
      this.ngZone.run(() => this.validationError.set('FileReadError'));
    };

    reader.readAsArrayBuffer(rawFile);
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  public onDragLeave(): void {
    this.isDragOver.set(false);
  }

  public onDrop(event: DragEvent): void {
    const methodName = "onDrop";
    this.logger.info(methodName);

    event.preventDefault();
    this.isDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setFile(file);
    }
  }

  private setFile(file: File): void {
    const methodName = "setFile";

    const error = this.documentPrintService.validateFile(file);
    this.validationError.set(error);
    if (!error) {
      this.selectedFile.set(file);
      this.logger.info('ShareExtension: file selected name=' + this.sanitizationService.sanitizeLogOutput(file.name) + ' size=' + file.size);
    } else {
      this.selectedFile.set(null);
      this.logger.warn(methodName, 'ShareExtension: validation FAILED — selectedFile set to null. error=' + error);
    }
  }

  // ############### UPLOAD ###############

  public get canUpload(): boolean {
    return (
      this.selectedFile() !== null &&
      this.selectedQueue() !== null &&
      this.uploadState === 'idle'
    );
  }

  public onUpload(): void {
    const methodName = "onUpload";
    const file  = this.selectedFile();
    const queue = this.selectedQueue();
    const user  = this.userService.userSignal();
    this.logger.info(methodName + ' canUpload=' + this.canUpload
      + ' uploadState=' + this.uploadState
      + ' file=' + (file ? this.sanitizationService.sanitizeLogOutput(file.name) + '(' + file.size + 'b)' : 'null')
      + ' queue=' + (queue ? queue.name : 'null')
      + ' user=' + (user ? 'ok' : 'null'));

    if (!file || !queue || !user) {
      this.logger.warn(methodName, 'onUpload: guard blocked — file=' + !!file + ' queue=' + !!queue + ' user=' + !!user);
      return;
    }

    const printJobsUrl = this.sanitizationService.validateApiUrl(user.links.printJobs);
    const printerUrl   = this.sanitizationService.validateApiUrl(queue.links.printer);
    const queueUrl     = this.sanitizationService.validateApiUrl(queue.links.self);

    if (!printJobsUrl || !printerUrl || !queueUrl) {
      this.logger.error(methodName, 'Invalid HATEOAS URL(s) — aborting upload');
      return;
    }

    this.documentPrintService.uploadDocument(
      printJobsUrl,
      printerUrl,
      queueUrl,
      file
    ).pipe(takeUntil(this.destroy$))
    .subscribe({
      next: () => {
        this.documentPrintService.uploadState.set('done');
        if (this.platformService.isIOS()) {
          this.shareExtensionService.deleteSharedFile();
        }
        void this.router.navigate(['/print-jobs']);
      },
      error: () => {
        // already handled by DocumentPrintService.uploadDocument catchError
      }
    });
  }
}
